import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    addStickyNote,
    editStickyNote,
    getStickyNotesMode,
    loadStickyNotes,
    normalizeStickyNote,
    removeStickyNote,
    sortStickyNotes,
    subscribeStickyNotesMode,
} from './stickyNotesStore';

/**
 * Sticky notes for the orders on screen, keyed by order number.
 * Loads each order once; later changes arrive through add/update/remove here or
 * through `applyRemote` (another user's save, relayed by the presence socket).
 *
 * `onChange(orderNumber, notes)` fires after a local save so the page can tell
 * everyone else. It is skipped in local mode, where notes are not shared.
 */
export default function useStickyNotes(orderNumbers, { onChange } = {}) {
    const [notesByOrder, setNotesByOrder] = useState({});
    const [mode, setMode] = useState(getStickyNotesMode);
    const [loadError, setLoadError] = useState(null);
    const notesRef = useRef({});
    const loadedRef = useRef(new Set());
    const mountedRef = useRef(true);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        mountedRef.current = true;
        const unsubscribe = subscribeStickyNotesMode(setMode);
        return () => {
            mountedRef.current = false;
            unsubscribe();
        };
    }, []);

    const signature = useMemo(
        () => Array.from(new Set((orderNumbers || []).filter(Boolean).map(String))).sort().join('|'),
        [orderNumbers]
    );

    const apply = useCallback((orderNumber, notes, broadcast) => {
        const key = String(orderNumber);
        notesRef.current = { ...notesRef.current, [key]: notes };
        if (mountedRef.current) setNotesByOrder(notesRef.current);
        if (broadcast && getStickyNotesMode() === 'remote') onChangeRef.current?.(key, notes);
    }, []);

    useEffect(() => {
        const wanted = signature ? signature.split('|') : [];
        const missing = wanted.filter((n) => !loadedRef.current.has(n));
        if (!missing.length) return;
        missing.forEach((n) => loadedRef.current.add(n));
        loadStickyNotes(missing)
            .then((map) => {
                notesRef.current = { ...notesRef.current, ...map };
                if (mountedRef.current) {
                    setNotesByOrder(notesRef.current);
                    setLoadError(null);
                }
            })
            .catch(() => {
                missing.forEach((n) => loadedRef.current.delete(n));
                if (mountedRef.current) setLoadError('Sticky notes could not be loaded. Refresh the page to try again.');
            });
    }, [signature]);

    const add = useCallback(async (orderNumber, fields) => {
        const note = await addStickyNote({ orderNumber, ...fields });
        apply(orderNumber, sortStickyNotes([note, ...(notesRef.current[String(orderNumber)] || [])]), true);
        return note;
    }, [apply]);

    const update = useCallback(async (orderNumber, note, changes) => {
        const saved = await editStickyNote(note, changes);
        const current = notesRef.current[String(orderNumber)] || [];
        apply(orderNumber, current.map((n) => (n.id === note.id ? saved : n)), true);
        return saved;
    }, [apply]);

    const remove = useCallback(async (orderNumber, note) => {
        await removeStickyNote(note);
        const current = notesRef.current[String(orderNumber)] || [];
        apply(orderNumber, current.filter((n) => n.id !== note.id), true);
    }, [apply]);

    const applyRemote = useCallback((orderNumber, notes) => {
        if (orderNumber == null || !Array.isArray(notes)) return;
        apply(orderNumber, sortStickyNotes(notes.map(normalizeStickyNote).filter(Boolean)), false);
    }, [apply]);

    return { notesByOrder, mode, loadError, add, update, remove, applyRemote };
}
